import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  inject,
  OnInit,
  Renderer2,
  signal,
  ViewChild
} from '@angular/core';
import { IMAGE_TOKEN } from './lightbox.tokens';
import { LightboxRef } from './lightbox-ref';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ImageGallerySelector } from './lightbox.service';
import { PhotoReference } from '../../../model/job.model';
import { UserService } from '../../../services/user.service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { NoticeMedia } from '../../notice-board/notice.model';
import { NoticeService } from '../../../services/notice.service';
import { MatIcon } from '@angular/material/icon';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DatePipe } from '../../../pipes/date.pipe';

@Component({
  selector: 'app-lightbox',
  templateUrl: './lightbox.component.html',
  styleUrls: ['./lightbox.component.scss'],
  animations: [
    trigger('fade', [
      state('fadeOut', style({ opacity: 0 })),
      state('fadeIn', style({ opacity: 1 })),
      transition('* => fadeIn', animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)'))
    ])
  ],
  imports: [MatIcon, CdkDrag, MatProgressSpinner, DatePipe]
})
export class LightboxComponent implements OnInit {
  @ViewChild('imageElement', { read: ElementRef, static: false }) imageElement: ElementRef;
  zoomUnit = 1;
  initialImagePosition = null;
  tooltipIsVisible = false;
  selectedImage: PhotoReference | NoticeMedia;
  selectedImgCaptureTime;
  selectedImgTakenBy;
  selectedIndex;

  selectedImgSrc = signal<string | null>(null);
  loading = signal<boolean>(true);

  #message = inject(MessagingService);
  #userService = inject(UserService);
  #noticeService = inject(NoticeService);

  constructor(
    private renderer: Renderer2,
    public boxRef: LightboxRef,
    @Inject(IMAGE_TOKEN) public imageGallerySelector: ImageGallerySelector
  ) {}

  ngOnInit(): void {
    this.updateSelectedImage(this.imageGallerySelector.selectedIndex);
  }

  updateSelectedImage(index: number) {
    const { images, isJobMedia } = this.imageGallerySelector;
    const selectedImage = images[index];
    (isJobMedia
      ? this.#userService.getPhotoUrl(selectedImage.id)
      : this.#noticeService.getNoticeMediaUrl(selectedImage.id)
    ).subscribe({
      next: (url) => this.#updateSelectedImageCallback(index, selectedImage, url),
      error: () => this.#getMediaError()
    });
  }

  #updateSelectedImageCallback(index: number, selectedImage: PhotoReference | NoticeMedia, url: string): void {
    if (url) {
      const { isJobMedia } = this.imageGallerySelector;
      this.selectedIndex = index;
      this.selectedImage = selectedImage;
      this.selectedImgSrc.set(url);
      this.selectedImgCaptureTime = isJobMedia ? (selectedImage as PhotoReference).captureTime : '';
      this.selectedImgTakenBy = isJobMedia ? (selectedImage as PhotoReference).takenBy : '';
    } else {
      this.#getMediaError();
    }
  }

  #getMediaError(): void {
    this.#message.error('There was an error loading the image.');
    this.close();
  }

  previous() {
    this.selectedIndex === 0
      ? this.updateSelectedImage(this.imageGallerySelector.images.length - 1)
      : this.updateSelectedImage(this.selectedIndex - 1);
  }

  next() {
    this.selectedIndex === this.imageGallerySelector.images.length - 1
      ? this.updateSelectedImage(0)
      : this.updateSelectedImage(this.selectedIndex + 1);
  }

  wheelScroll(event) {
    const delta = Math.max(-1, Math.min(1, event.wheelDelta || event.deltaY || -event.detail));
    this.zoomUnit = Math.max(0.3, this.zoomUnit + 0.3 * delta);
    this.resizeImage();
  }

  zoomIn() {
    this.zoomUnit = this.zoomUnit + 0.3;
    this.resizeImage();
  }

  zoomOut() {
    this.zoomUnit = Math.max(0.3, this.zoomUnit - 0.3);
    this.resizeImage();
  }

  resizeImage() {
    this.renderer.setStyle(this.imageElement.nativeElement, 'transform', 'scale(' + this.zoomUnit + ')');
  }

  reset() {
    this.renderer.setStyle(this.imageElement.nativeElement, 'transform', 'scale(1)');
    this.zoomUnit = 1;
    this.initialImagePosition = { x: 0, y: 0 };
  }

  close() {
    this.boxRef.close();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this.previous();
        break;
      case 'ArrowRight':
        this.next();
        break;
    }
  }
}
